import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, Loader2, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";
import { normalizeTime } from "../../lib/timeUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact schedule modal used from the Job detail toolbar. Wraps the same
 * /admin/jobs/{id}/confirm-schedule endpoint used by the Awaiting Scheduling
 * page so behaviour stays identical — sets fitment_date + fitter and flips
 * status to 'scheduled'.
 */
export const JobScheduleModal = ({ job, onClose, onSaved }) => {
  const [fitters, setFitters] = useState([]);
  const [form, setForm] = useState({
    fitment_date: (job.fitment_date || "").slice(0, 10) || "",
    fitment_time: normalizeTime(job.fitment_time),
    fitter_team_leader: job.fitter_team_leader || "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/fitter-users`).then((r) => setFitters(r.data || []))
      .catch(() => setFitters([]));
  }, []);

  const save = async () => {
    if (!form.fitment_date) return toast.error("Choose a fitment date");
    setBusy(true);
    try {
      await axios.post(`${API}/admin/jobs/${job.id}/confirm-schedule`, form);
      toast.success("Fitment scheduled");
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not schedule");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="job-schedule-modal">
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">Schedule fitment</div>
            <h2 className="font-display font-black uppercase text-lg text-[#111619] mt-1">
              Job #{job.job_number} · {job.registration || "—"}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="job-schedule-close" className="text-[#3f4b52]">
            <X size={18} />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Fitment date</label>
            <input type="date" value={form.fitment_date}
                   onChange={(e) => setForm({ ...form, fitment_date: e.target.value })}
                   className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
                   data-testid="job-schedule-date" />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Fitment time</label>
            <input type="time" value={form.fitment_time}
                   onChange={(e) => setForm({ ...form, fitment_time: e.target.value })}
                   className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
                   data-testid="job-schedule-time" />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">Fitter / team leader</label>
            <select value={form.fitter_team_leader}
                    onChange={(e) => setForm({ ...form, fitter_team_leader: e.target.value })}
                    className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
                    data-testid="job-schedule-fitter">
              <option value="">Choose fitter (optional)…</option>
              {fitters.map((f) => (
                <option key={f.id} value={f.name || f.email}>{f.name || f.email}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]">
            Cancel
          </button>
          <button onClick={save} disabled={busy || !form.fitment_date}
                  data-testid="job-schedule-save"
                  className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CalendarCheck2 size={14} />}
            Schedule fitment
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobScheduleModal;
